import { ErrorHandler, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AuthService } from "./auth.service";
import Swal from 'sweetalert2';
import { 
  getFirestore, 
  disableNetwork, 
  enableNetwork 
} from 'firebase/firestore';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private firestoreErrorCount = 0;
  private lastResetTime = 0;
  private isResetting = false;
  private db = getFirestore();

  constructor(
    private AuthService: AuthService,
    private router: Router, 
    private afAuth: AngularFireAuth
  ) {}

  handleError(error: any): void {
    console.error('Global error caught:', error);
    
    const errorJson = this.isFirebasePermissionDenied(error);
    
    // تحقق من نوع الخطأ
    if (this.isPermissionDeniedError(errorJson)) {
      console.log('🔐 Permission denied error detected');
      this.logout();
    } else if (this.isFirestoreConnectionError(error)) {
      console.log('🔗 Firestore connection error detected');
      this.handleFirestoreConnectionError(error);
    } else if (this.isFirestoreError(error)) {
      console.log('🔥 Other Firestore error detected');
      this.handleFirestoreError(error);
    } else {
      console.error('❓ Unknown error occurred:', error);
    }
  }

  // تحقق من خطأ الصلاحيات
  private isPermissionDeniedError(errorJson: any): boolean {
    return errorJson?.error?.stack?.[1] === "FirebaseError: Missing or insufficient permissions." ||
           errorJson?.error?.code === 'permission-denied' ||
           errorJson?.error?.message?.includes('Missing or insufficient permissions');
  }

  // تحقق من أخطاء اتصال Firestore
  private isFirestoreConnectionError(error: any): boolean {
    return error?.message?.includes('Listen') ||
           error?.message?.includes('channel') ||
           error?.code === 'unavailable' ||
           error?.code === 'deadline-exceeded' ||
           error?.message?.includes('Failed to get document') ||
           error?.message?.includes('DEADLINE_EXCEEDED');
  }

  // تحقق من أخطاء Firestore عامة
  private isFirestoreError(error: any): boolean {
    return typeof error?.message === 'string' && (error.message.includes('firestore') || error.message.includes('Firebase')) ||
           (typeof error?.code === 'string' && error.code.startsWith('firestore/')) ||
           (typeof error?.name === 'string' && error.name.includes('FirebaseError'));
  }

  // معالجة أخطاء اتصال Firestore
  private handleFirestoreConnectionError(error: any) {
    this.firestoreErrorCount++;
    
    console.log(`Firestore connection error count: ${this.firestoreErrorCount}`);
    
    // لو كتر الأخطاء ومعدلش الاتصال من فترة
    if (this.firestoreErrorCount >= 3 && 
        Date.now() - this.lastResetTime > 30000 && 
        !this.isResetting) {
      
      console.log('🔄 Multiple Firestore connection errors, attempting reset...');
      this.resetFirestoreConnection();
    }
  }

  // معالجة أخطاء Firestore عامة
  private handleFirestoreError(error: any) {
    // سجل الخطأ بس ماتعملش حاجة كتير
    console.warn('Firestore error handled:', {
      code: error?.code,
      message: error?.message?.substring(0, 100) + '...'
    });
  }

  // إعادة تشغيل اتصال Firestore
  private async resetFirestoreConnection() {
    if (this.isResetting) {
      console.log('Connection reset already in progress...');
      return;
    }

    this.isResetting = true;
    this.lastResetTime = Date.now();

    try {
      console.log('🔄 Resetting Firestore connection...');
      
      // فصل الشبكة
      await disableNetwork(this.db);
      
      // انتظار
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // إعادة تشغيل الشبكة
      await enableNetwork(this.db);
      
      // انتظار إضافي للتأكد
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      this.firestoreErrorCount = 0;
      console.log('✅ Firestore connection reset completed');
      
    } catch (resetError) {
      console.error('❌ Error during Firestore connection reset:', resetError);
    } finally {
      this.isResetting = false;
    }
  }

  // تسجيل خروج المستخدم
  private async logout() {
    try {
      await Swal.fire({
        icon: "error",
        title: "Oops...",
        text: 'Subscription Renewal Needed',
        confirmButtonText: 'OK',
        allowOutsideClick: false
      });
      
      this.AuthService.logout();
    } catch (swalError) {
      console.error('Error showing logout message:', swalError);
      // Fallback logout
      this.AuthService.logout();
    }
  }

  // تحليل خطأ Firebase وإرجاع JSON
  private isFirebasePermissionDenied(error: any): any {
    const errorJson = {
      error: {
        code: error?.code || 'unknown',
        message: error?.message || 'An unknown error occurred.',
        stack: error?.stack ? error.stack.split('\n') : [],
        name: error?.name || 'UnknownError'
      },
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
    
    // سجل الخطأ بتفاصيل أكتر
    console.group('🚨 Firebase Error Details');
    console.error('Raw error:', error);
    console.error('Processed error:', JSON.stringify(errorJson, null, 2));
    console.groupEnd();

    return errorJson;
  }

  // إحصائيات للمراقبة (للتطوير)
  getFirestoreErrorStats() {
    return {
      errorCount: this.firestoreErrorCount,
      lastResetTime: new Date(this.lastResetTime).toLocaleString(),
      isResetting: this.isResetting
    };
  }

  // إعادة تعيين عداد الأخطاء (للاستخدام الخارجي)
  resetErrorCount() {
    this.firestoreErrorCount = 0;
    console.log('Firestore error count reset');
  }
}